'use client';

import { useEffect, useRef } from 'react';

export function Starfield({
  jump,
  onJumpComplete,
}: {
  jump: boolean;
  onJumpComplete?: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const starsRef = useRef<{ x: number; y: number; z: number }[]>([]);
  const jumpRef = useRef(false);
  const jumpStartRef = useRef(0);
  const onCompleteRef = useRef(onJumpComplete);
  onCompleteRef.current = onJumpComplete;

  // Init stars once
  useEffect(() => {
    const stars = [];
    for (let i = 0; i < 800; i++) {
      stars.push({
        x: (Math.random() - 0.5) * 2000,
        y: (Math.random() - 0.5) * 2000,
        z: Math.random() * 1000,
      });
    }
    starsRef.current = stars;
  }, []);

  // Trigger jump
  useEffect(() => {
    if (jump && !jumpRef.current) {
      jumpRef.current = true;
      jumpStartRef.current = Date.now();
    }
  }, [jump]);

  // Main animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let frame = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const animate = () => {
      const w = canvas.width;
      const h = canvas.height;
      const cx = w / 2;
      const cy = h / 2;
      const stars = starsRef.current;

      let speed = 0.5;
      let isJumping = jumpRef.current;
      let flashAmount = 0;

      if (isJumping) {
        const elapsed = Date.now() - jumpStartRef.current;
        if (elapsed < 4000) {
          // Single smooth curve: 0.5 -> 80 over 4 seconds
          const t = elapsed / 4000;
          // Exponential ease-in for smooth acceleration
          speed = 0.5 + Math.pow(t, 2.5) * 80;
        } else if (elapsed < 4800) {
          speed = 80;
          flashAmount = (elapsed - 4000) / 800;
        } else {
          onCompleteRef.current?.();
          return;
        }
      }

      // Trail fade — shorter trails at low speed, longer at high speed
      const fadeAlpha = isJumping ? Math.max(0.03, 0.15 - speed / 800) : 0.2;
      ctx.fillStyle = `rgba(0, 0, 0, ${fadeAlpha})`;
      ctx.fillRect(0, 0, w, h);

      for (const star of stars) {
        star.z -= speed;
        if (star.z <= 0) {
          star.x = (Math.random() - 0.5) * 2000;
          star.y = (Math.random() - 0.5) * 2000;
          star.z = 1000;
        }

        const sx = (star.x / star.z) * 300 + cx;
        const sy = (star.y / star.z) * 300 + cy;
        if (sx < 0 || sx > w || sy < 0 || sy > h) continue;

        const size = Math.max(0.5, (1 - star.z / 1000) * 2.5);
        const brightness = Math.min(1, (1 - star.z / 1000) * 1.5);

        // Blend between dots and streaks based on speed
        const streakMix = Math.min(1, Math.max(0, (speed - 1) / 5)); // 0 at speed<=1, 1 at speed>=6

        if (streakMix > 0) {
          const prevZ = star.z + speed;
          const px = (star.x / prevZ) * 300 + cx;
          const py = (star.y / prevZ) * 300 + cy;
          ctx.strokeStyle = `rgba(180, 200, 255, ${brightness * streakMix})`;
          ctx.lineWidth = Math.min(size * 0.8, 2);
          ctx.beginPath();
          ctx.moveTo(px, py);
          ctx.lineTo(sx, sy);
          ctx.stroke();
        }
        if (streakMix < 1) {
          ctx.fillStyle = `rgba(200, 210, 255, ${brightness * (1 - streakMix)})`;
          ctx.beginPath();
          ctx.arc(sx, sy, size, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // White flash
      if (flashAmount > 0) {
        ctx.fillStyle = `rgba(255, 255, 255, ${Math.min(1, flashAmount)})`;
        ctx.fillRect(0, 0, w, h);
      }

      frame = requestAnimationFrame(animate);
    };

    frame = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
      }}
    />
  );
}
